/**
 * blePrinter.ts
 *
 * Bridge BLE nativo via @capacitor-community/bluetooth-le.
 * Dynamic import para no romper el build web.
 * Usado solo en APK Android cuando el usuario elige impresora Bluetooth externa.
 *
 * IMPORTANTE: Muchas impresoras térmicas portátiles usan Bluetooth Clásico (SPP),
 * NO BLE. Si el scan no encuentra la impresora, o connectAndDiscover no encuentra
 * servicios escribibles, probablemente la impresora no es BLE.
 * Los logs de diagnóstico indican claramente esta situación.
 */

import { toEscPosBytes, CANDIDATE_SERVICE_UUIDS, CANDIDATE_CHARACTERISTICS } from './escposPrinter';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BleDevice {
  deviceId: string;
  name: string | null;
  /** UUIDs advertidos por el dispositivo (si los reporta el scan) */
  uuids?: string[];
}

export interface SavedBlePrinter {
  deviceId: string;
  name: string;
  serviceUuid: string;
  characteristicUuid: string;
  /** 'write' = con respuesta, 'writeWithoutResponse' = sin respuesta */
  writeMode: 'write' | 'writeWithoutResponse';
}

export interface BlePrintResult {
  ok: boolean;
  error?: string;
}

const LS_BLE_PRINTER_KEY = 'tiempos_ble_printer';
const TAG = '[BLE]';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function getBleClient() {
  const mod = await import('@capacitor-community/bluetooth-le');
  return mod.BleClient;
}

function toDataView(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

export async function requestBlePermissions(): Promise<void> {
  console.log(`${TAG} requestBlePermissions: initializing BLE...`);
  const BleClient = await getBleClient();
  await BleClient.initialize({ androidNeverForLocation: true });
  console.log(`${TAG} requestBlePermissions: OK`);
}

// ---------------------------------------------------------------------------
// Scan
// ---------------------------------------------------------------------------

export async function scanForPrinters(timeoutMs = 8000): Promise<BleDevice[]> {
  const BleClient = await getBleClient();
  await BleClient.initialize({ androidNeverForLocation: true });

  const devices: BleDevice[] = [];
  const seen = new Set<string>();

  console.log(`${TAG} SCAN: fase 1 — filtrando por UUIDs de impresora conocidos...`);
  console.log(`${TAG} SCAN: UUIDs filtro:`, CANDIDATE_SERVICE_UUIDS);

  await BleClient.requestLEScan(
    { services: CANDIDATE_SERVICE_UUIDS, allowDuplicates: false },
    (result) => {
      if (result.device && !seen.has(result.device.deviceId)) {
        seen.add(result.device.deviceId);
        const dev: BleDevice = {
          deviceId: result.device.deviceId,
          name: result.localName || result.device.name || null,
          uuids: result.uuids || result.device.uuids,
        };
        devices.push(dev);
        console.log(`${TAG} SCAN: encontrado (fase 1):`, dev.name || '(sin nombre)', dev.deviceId, 'uuids:', dev.uuids || '(ninguno)');
      }
    },
  );

  await delay(timeoutMs);
  await BleClient.stopLEScan();
  console.log(`${TAG} SCAN: fase 1 terminada, ${devices.length} dispositivo(s) encontrado(s)`);

  // Si el scan filtrado no encontró nada, intentar sin filtro
  if (devices.length === 0) {
    console.log(`${TAG} SCAN: fase 2 — scan abierto sin filtro de servicio...`);
    console.log(`${TAG} SCAN: NOTA — si no aparece tu impresora aqui, probablemente usa Bluetooth Clasico (SPP), no BLE.`);

    await BleClient.requestLEScan(
      { allowDuplicates: false },
      (result) => {
        if (result.device && !seen.has(result.device.deviceId)) {
          seen.add(result.device.deviceId);
          const dev: BleDevice = {
            deviceId: result.device.deviceId,
            name: result.localName || result.device.name || null,
            uuids: result.uuids || result.device.uuids,
          };
          devices.push(dev);
          console.log(`${TAG} SCAN: encontrado (fase 2):`, dev.name || '(sin nombre)', dev.deviceId, 'uuids:', dev.uuids || '(ninguno)');
        }
      },
    );

    await delay(timeoutMs);
    await BleClient.stopLEScan();
    console.log(`${TAG} SCAN: fase 2 terminada, ${devices.length} total`);
  }

  if (devices.length === 0) {
    console.warn(`${TAG} SCAN: NINGUN dispositivo BLE encontrado.`);
    console.warn(`${TAG} SCAN: Posibles causas:`);
    console.warn(`${TAG} SCAN:   1. La impresora usa Bluetooth Clasico (SPP), no BLE`);
    console.warn(`${TAG} SCAN:   2. La impresora esta apagada o fuera de rango`);
    console.warn(`${TAG} SCAN:   3. Bluetooth no esta habilitado en el dispositivo`);
  }

  return devices;
}

// ---------------------------------------------------------------------------
// Connect & discover
// ---------------------------------------------------------------------------

export async function connectAndDiscover(deviceId: string): Promise<SavedBlePrinter> {
  const BleClient = await getBleClient();
  await BleClient.initialize({ androidNeverForLocation: true });

  console.log(`${TAG} CONNECT: conectando a ${deviceId}...`);
  await BleClient.connect(deviceId, () => {
    console.log(`${TAG} CONNECT: desconexion callback para ${deviceId}`);
  });
  console.log(`${TAG} CONNECT: conectado OK`);

  try {
    console.log(`${TAG} DISCOVER: obteniendo servicios...`);
    const services = await BleClient.getServices(deviceId);
    console.log(`${TAG} DISCOVER: ${services.length} servicio(s) encontrado(s)`);

    // Log completo de servicios y características para diagnóstico
    for (const svc of services) {
      console.log(`${TAG} DISCOVER:   servicio: ${svc.uuid} (${svc.characteristics.length} caracteristica(s))`);
      for (const chr of svc.characteristics) {
        const props = chr.properties;
        const flags: string[] = [];
        if (props.read) flags.push('READ');
        if (props.write) flags.push('WRITE');
        if (props.writeWithoutResponse) flags.push('WRITE_NO_RESP');
        if (props.notify) flags.push('NOTIFY');
        if (props.indicate) flags.push('INDICATE');
        console.log(`${TAG} DISCOVER:     chr: ${chr.uuid} [${flags.join(', ')}]`);
      }
    }

    // Buscar por candidatos conocidos primero
    for (const sUuid of CANDIDATE_SERVICE_UUIDS) {
      const svc = services.find((s) => s.uuid.toLowerCase() === sUuid.toLowerCase());
      if (!svc) continue;
      console.log(`${TAG} DISCOVER: servicio candidato encontrado: ${svc.uuid}`);

      for (const cUuid of CANDIDATE_CHARACTERISTICS) {
        const chr = svc.characteristics.find(
          (c) => c.uuid.toLowerCase() === cUuid.toLowerCase(),
        );
        if (!chr) continue;
        const writeMode = resolveWriteMode(chr.properties);
        if (writeMode) {
          const name = (await getDeviceName(BleClient, deviceId)) || deviceId;
          console.log(`${TAG} DISCOVER: MATCH candidato — svc=${svc.uuid} chr=${chr.uuid} mode=${writeMode}`);
          return { deviceId, name, serviceUuid: svc.uuid, characteristicUuid: chr.uuid, writeMode };
        }
      }
      // Fallback: cualquier characterística escribible en este servicio
      for (const chr of svc.characteristics) {
        const writeMode = resolveWriteMode(chr.properties);
        if (writeMode) {
          const name = (await getDeviceName(BleClient, deviceId)) || deviceId;
          console.log(`${TAG} DISCOVER: MATCH fallback en servicio candidato — svc=${svc.uuid} chr=${chr.uuid} mode=${writeMode}`);
          return { deviceId, name, serviceUuid: svc.uuid, characteristicUuid: chr.uuid, writeMode };
        }
      }
    }

    // Fallback global: cualquier servicio con característica escribible
    for (const svc of services) {
      for (const chr of svc.characteristics) {
        const writeMode = resolveWriteMode(chr.properties);
        if (writeMode) {
          const name = (await getDeviceName(BleClient, deviceId)) || deviceId;
          console.log(`${TAG} DISCOVER: MATCH global fallback — svc=${svc.uuid} chr=${chr.uuid} mode=${writeMode}`);
          return { deviceId, name, serviceUuid: svc.uuid, characteristicUuid: chr.uuid, writeMode };
        }
      }
    }

    console.error(`${TAG} DISCOVER: NO se encontro NINGUNA caracteristica escribible`);
    console.error(`${TAG} DISCOVER: Esto puede indicar:`);
    console.error(`${TAG} DISCOVER:   1. El dispositivo no es una impresora BLE`);
    console.error(`${TAG} DISCOVER:   2. Es Bluetooth Clasico (SPP) disfrazado de BLE`);
    console.error(`${TAG} DISCOVER:   3. Requiere emparejamiento previo desde la configuracion de Android`);
    throw new Error('No se encontro caracteristica BLE escribible. La impresora podria no ser compatible con BLE.');
  } catch (err) {
    try { await BleClient.disconnect(deviceId); } catch { /* ignore disconnect error */ }
    throw err;
  }
}

/** Determina el mejor modo de escritura según propiedades de la característica */
function resolveWriteMode(
  props: { write?: boolean; writeWithoutResponse?: boolean },
): 'write' | 'writeWithoutResponse' | null {
  // Preferir writeWithoutResponse — es lo que la mayoria de impresoras POS BLE usan
  if (props.writeWithoutResponse) return 'writeWithoutResponse';
  if (props.write) return 'write';
  return null;
}

async function getDeviceName(BleClient: any, deviceId: string): Promise<string | null> {
  try {
    const devices = await BleClient.getConnectedDevices(CANDIDATE_SERVICE_UUIDS);
    const dev = devices.find((d: any) => d.deviceId === deviceId);
    return dev?.name || null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Core BLE write with correct mode + chunking + delay
// ---------------------------------------------------------------------------

async function bleWriteChunked(
  BleClient: any,
  deviceId: string,
  serviceUuid: string,
  characteristicUuid: string,
  bytes: Uint8Array,
  writeMode: 'write' | 'writeWithoutResponse',
  chunkSize = 100,
  delayMs = 20,
): Promise<void> {
  const totalChunks = Math.ceil(bytes.length / chunkSize);
  console.log(`${TAG} WRITE: ${bytes.length} bytes, ${totalChunks} chunks (${chunkSize}B), mode=${writeMode}, delay=${delayMs}ms`);

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    const chunkNum = Math.floor(i / chunkSize) + 1;
    const dv = toDataView(chunk);

    try {
      if (writeMode === 'writeWithoutResponse') {
        await BleClient.writeWithoutResponse(deviceId, serviceUuid, characteristicUuid, dv);
      } else {
        await BleClient.write(deviceId, serviceUuid, characteristicUuid, dv);
      }
    } catch (err: any) {
      console.error(`${TAG} WRITE: chunk ${chunkNum}/${totalChunks} FALLO (${chunk.length}B):`, err?.message || err);
      throw new Error(`Error en chunk ${chunkNum}/${totalChunks}: ${err?.message || err}`);
    }

    // Delay entre chunks para dar tiempo al buffer de la impresora
    if (i + chunkSize < bytes.length && delayMs > 0) {
      await delay(delayMs);
    }
  }

  console.log(`${TAG} WRITE: todos los chunks enviados OK`);
}

// ---------------------------------------------------------------------------
// Print via BLE
// ---------------------------------------------------------------------------

export async function printViaBle(
  text: string,
  printer: SavedBlePrinter,
): Promise<BlePrintResult> {
  const BleClient = await getBleClient();
  await BleClient.initialize({ androidNeverForLocation: true });

  console.log(`${TAG} PRINT: inicio`);
  console.log(`${TAG} PRINT: dispositivo=${printer.name} (${printer.deviceId})`);
  console.log(`${TAG} PRINT: svc=${printer.serviceUuid} chr=${printer.characteristicUuid}`);
  console.log(`${TAG} PRINT: writeMode=${printer.writeMode || '(no guardado, usando fallback)'}`);
  console.log(`${TAG} PRINT: texto=${text.length} chars`);

  const writeMode = printer.writeMode || 'writeWithoutResponse';

  try {
    console.log(`${TAG} PRINT: conectando...`);
    await BleClient.connect(printer.deviceId, () => {
      console.log(`${TAG} PRINT: desconexion callback`);
    });
    console.log(`${TAG} PRINT: conectado OK`);

    const bytes = toEscPosBytes(text, 80);
    console.log(`${TAG} PRINT: ESC/POS bytes=${bytes.length}`);

    await bleWriteChunked(
      BleClient,
      printer.deviceId,
      printer.serviceUuid,
      printer.characteristicUuid,
      bytes,
      writeMode,
      100,  // chunkSize: 100 bytes (seguro para la mayoría de impresoras BLE)
      20,   // delayMs: 20ms entre chunks
    );

    console.log(`${TAG} PRINT: completado OK`);
    return { ok: true };
  } catch (err: any) {
    const msg = err?.message || String(err);
    console.error(`${TAG} PRINT: ERROR:`, msg);

    // Si falló con write, intentar con writeWithoutResponse y viceversa
    if (writeMode === 'write') {
      console.warn(`${TAG} PRINT: reintentando con writeWithoutResponse...`);
      try {
        // Ya estamos conectados o reconectamos
        try {
          await BleClient.connect(printer.deviceId, () => {});
        } catch { /* ya conectado */ }

        const bytes = toEscPosBytes(text, 80);
        await bleWriteChunked(
          BleClient, printer.deviceId,
          printer.serviceUuid, printer.characteristicUuid,
          bytes, 'writeWithoutResponse', 100, 20,
        );
        console.log(`${TAG} PRINT: reintento con writeWithoutResponse OK`);
        return { ok: true };
      } catch (retryErr: any) {
        console.error(`${TAG} PRINT: reintento tambien fallo:`, retryErr?.message);
        return { ok: false, error: `${msg} (reintento: ${retryErr?.message})` };
      }
    }

    return { ok: false, error: msg };
  } finally {
    try {
      await BleClient.disconnect(printer.deviceId);
      console.log(`${TAG} PRINT: desconectado`);
    } catch { /* ignore */ }
  }
}

// ---------------------------------------------------------------------------
// Print raw ESC/POS bytes via BLE (pre-formatted, no text conversion)
// ---------------------------------------------------------------------------

export async function printRawBytesViaBle(
  bytes: Uint8Array,
  printer: SavedBlePrinter,
): Promise<BlePrintResult> {
  const BleClient = await getBleClient();
  await BleClient.initialize({ androidNeverForLocation: true });

  console.log(`${TAG} PRINT-RAW: inicio (${bytes.length} bytes)`);
  console.log(`${TAG} PRINT-RAW: dispositivo=${printer.name} (${printer.deviceId})`);

  const writeMode = printer.writeMode || 'writeWithoutResponse';

  try {
    await BleClient.connect(printer.deviceId, () => {
      console.log(`${TAG} PRINT-RAW: desconexion callback`);
    });

    await bleWriteChunked(
      BleClient,
      printer.deviceId,
      printer.serviceUuid,
      printer.characteristicUuid,
      bytes,
      writeMode,
      100,
      20,
    );

    console.log(`${TAG} PRINT-RAW: completado OK`);
    return { ok: true };
  } catch (err: any) {
    const msg = err?.message || String(err);
    console.error(`${TAG} PRINT-RAW: ERROR:`, msg);
    return { ok: false, error: msg };
  } finally {
    try {
      await BleClient.disconnect(printer.deviceId);
    } catch { /* ignore */ }
  }
}

// ---------------------------------------------------------------------------
// Test print mínimo — para diagnosticar si BLE funciona
// ---------------------------------------------------------------------------

export async function testPrintBle(printer: SavedBlePrinter): Promise<BlePrintResult> {
  console.log(`${TAG} TEST: impresion de prueba minima`);
  // Texto muy corto para aislar problemas de transporte vs contenido
  const testText = 'TEST BLE\n----\nOK\n\n\n';
  return printViaBle(testText, printer);
}

// ---------------------------------------------------------------------------
// Saved printer persistence
// ---------------------------------------------------------------------------

export function getSavedBlePrinter(): SavedBlePrinter | null {
  try {
    const raw = localStorage.getItem(LS_BLE_PRINTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedBlePrinter;
    // Migrar printers guardadas sin writeMode
    if (!parsed.writeMode) {
      parsed.writeMode = 'writeWithoutResponse';
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveBlePrinter(printer: SavedBlePrinter): void {
  localStorage.setItem(LS_BLE_PRINTER_KEY, JSON.stringify(printer));
}

export function clearSavedBlePrinter(): void {
  localStorage.removeItem(LS_BLE_PRINTER_KEY);
}
